import { AxiosError, AxiosRequestConfig } from 'axios';
import { useTranslation } from 'react-i18next';
import { QueryKey, useQuery, UseQueryOptions } from 'react-query';
import { useDispatch } from 'react-redux';

import { addSnackbar, SnackbarPayload } from 'theSource/components';
import { api } from './api';
import { setAuthenticated } from 'theSource/features';
import { isSessionExpired, queryClient } from 'theSource/utils';

export interface UseApiQueryProps<TResponse, TError>
  extends UseQueryOptions<TResponse, AxiosError<TError>> {
  queryKey: QueryKey;
  endpoint: string;
  params?: object;
  config?: AxiosRequestConfig;
  onThen?: (data: TResponse) => TResponse;
  getErrorMessage?: (error: AxiosError<TError>) => string | undefined;
  showErrorSnackbar?: boolean;
  snackbarOptions?: Partial<SnackbarPayload>;
  apiVersion?: string;
}

export const useApiQuery = <TResponse, TError = unknown>({
  queryKey,
  endpoint,
  params,
  config,
  onThen,
  onSuccess,
  onError,
  getErrorMessage,
  showErrorSnackbar = true,
  snackbarOptions,
  ...queryOptions
}: UseApiQueryProps<TResponse, TError>) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  return useQuery<TResponse, AxiosError<TError>>(
    queryKey,
    () =>
      api
        .get<TResponse>(endpoint, params, config)
        .then((data) => (onThen ? onThen(data) : data)),
    {
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        if (isSessionExpired(error)) {
          dispatch(setAuthenticated(false));
          return;
        }

        if (error.response?.status === 500) {
          queryClient.cancelQueries(queryKey);
          queryClient.setQueryData(queryKey, undefined);
        }
        onError?.(error);

        if (showErrorSnackbar) {
          const errorMessage = getErrorMessage?.(error) ?? t('serverError')!;
          dispatch(
            addSnackbar({
              message: errorMessage,
              severity: 'error',
              disableAutoHide: true,
              ...snackbarOptions,
            })
          );
        }
      },
      ...queryOptions,
    }
  );
};
